import { MouseEventHandler, PropsWithChildren } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Checkbox } from 'primereact/checkbox'

import PrimeIcon from '../common/pure/PrimeIcon'
import ClassNameBuilder from '../../tools/ClassNameBuilder'

interface LinkProps {
    selected?: boolean
    disabled?: boolean
    className?: string
    to?: string
    onClick?: MouseEventHandler
}

interface NavigationLinkProps extends LinkProps, PropsWithChildren {}

export default function NavigationLink({
    className,
    selected,
    disabled,
    to,
    onClick,
    children,
}: NavigationLinkProps) {
    const location = useLocation()

    const logicallySelected = selected ?? (to ? location.pathname.includes(to) : false)
    const classNames = new ClassNameBuilder()
        .add('navigation-link')
        .addIf('selected', logicallySelected)
        .addIf('disabled', disabled)
        .add(className)

    const fullClassName = classNames.toString()
    const clickHandler = disabled ? undefined : onClick

    // If there's no `to` prop, just render a simple <div> instead,
    //  because the <Link> tag's `to` attribute is not optional.
    return to ? (
        <Link to={to} onClick={clickHandler} className={fullClassName}>
            {children}
        </Link>
    ) : (
        <div onClick={clickHandler} className={fullClassName}>
            {children}
        </div>
    )
}

interface NavigationIconLinkProps extends LinkProps {
    icon: PrimeIconName
    size?: number
}
/**
 * Render the navigation link using only a icon name and a text string.
 * Use the `to` prop to determine if the link is selected.
 * The `selected` prop takes precedence over this logic.
 */
export const NavigationIconLink = ({ icon, size, ...props }: NavigationIconLinkProps) => {
    return (
        <NavigationLink {...props}>
            <PrimeIcon name={icon} size={size ?? 16} />
        </NavigationLink>
    )
}

interface NavigationTextLinkProps extends LinkProps {
    icon?: PrimeIconName
    text?: string
    caret?: boolean
    strike?: boolean
}
export const NavigationTextLink = ({
    icon,
    text,
    caret,
    strike,
    ...props
}: NavigationTextLinkProps) => {
    return (
        <NavigationLink {...props}>
            {icon && <PrimeIcon name={icon} />}
            <div style={{ textDecoration: strike ? 'line-through' : 'none' }}>{text}</div>
            {caret && <PrimeIcon name="chevron-right" size={12} />}
        </NavigationLink>
    )
}

interface NavigationCheckboxLink extends NavigationTextLinkProps {}
export const NavigationCheckboxLink = ({ text, selected, ...props }: NavigationCheckboxLink) => {
    return (
        <NavigationLink {...props} selected={selected}>
            <Checkbox checked={selected ?? false} />
            {text}
        </NavigationLink>
    )
}
